import { useRef, useEffect, useCallback, useState } from 'react';

const FADE_DELAY = 400; // ms - koliko krug/strelica/tekst ostaju na agentu

export function AnnotationCanvas({ socket, sessionCode, videoRef, clientDimensions }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const previewRef = useRef(null);
  const localAnnotations = useRef([]);
  const [tool, setTool] = useState('pointer');
  const [color, setColor] = useState('#E24B4A');
  const [size, setSize] = useState(4);
  const [toolbarVisible, setToolbarVisible] = useState(true);

  const getVideoRect = useCallback(() => {
    const canvas = canvasRef.current;
    const cw = canvas?.width || 1;
    const ch = canvas?.height || 1;
    // Prioritet: dimenzije koje je telefon prijavio. Fallback: video.videoWidth.
    const video = videoRef?.current;
    const nativeW = clientDimensions?.width || video?.videoWidth || 0;
    const nativeH = clientDimensions?.height || video?.videoHeight || 0;
    if (!nativeW || !nativeH) {
      return { offX: 0, offY: 0, w: cw, h: ch };
    }
    const scale = Math.min(cw / nativeW, ch / nativeH);
    const w = nativeW * scale;
    const h = nativeH * scale;
    return { offX: (cw - w) / 2, offY: (ch - h) / 2, w, h };
  }, [videoRef, clientDimensions]);

  const toNorm = useCallback((x, y) => {
    const r = getVideoRect();
    return {
      x: Math.min(1, Math.max(0, (x - r.offX) / r.w)),
      y: Math.min(1, Math.max(0, (y - r.offY) / r.h))
    };
  }, [getVideoRect]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    localAnnotations.current.forEach(a => drawShape(ctx, a));
    if (previewRef.current) drawShape(ctx, previewRef.current);
  }, []);

  function drawShape(ctx, a) {
    ctx.strokeStyle = a.color;
    ctx.fillStyle = a.color;
    ctx.lineWidth = a.size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (a.type === 'arrow') {
      drawArrow(ctx, a.x1, a.y1, a.x2, a.y2, a.color, a.size);
    } else if (a.type === 'circle') {
      const rx = Math.abs(a.x2 - a.x1) / 2;
      const ry = Math.abs(a.y2 - a.y1) / 2;
      const cx = Math.min(a.x1, a.x2) + rx;
      const cy = Math.min(a.y1, a.y2) + ry;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx || 2, ry || 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (a.type === 'text') {
      ctx.font = `${a.size * 4 + 10}px sans-serif`;
      ctx.fillText(a.text, a.x, a.y);
    }
  }

  function drawArrow(ctx, x1, y1, x2, y2, color, size) {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = size;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headLen = 14 + size * 2;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  }

  const addLocalWithFade = (shape) => {
    localAnnotations.current.push(shape);
    redraw();
    setTimeout(() => {
      const idx = localAnnotations.current.indexOf(shape);
      if (idx !== -1) localAnnotations.current.splice(idx, 1);
      redraw();
    }, FADE_DELAY);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const onMouseMove = (e) => {
    if (tool === 'pointer') {
      const pos = getPos(e);
      const n = toNorm(pos.x, pos.y);
      const r = getVideoRect();
      console.log('mouse canvas:', pos.x.toFixed(0), pos.y.toFixed(0),
                  '| videoRect:', r.offX.toFixed(0), r.offY.toFixed(0), r.w.toFixed(0), r.h.toFixed(0),
                  '| norm:', n.x.toFixed(3), n.y.toFixed(3));
      socket?.emit('annotation', { code: sessionCode, data: {
        type: 'pointer', x: n.x, y: n.y
      } });
      return;
    }

    if (!isDrawing.current) return;
    const pos = getPos(e);

    if (tool === 'pen') {
      const n1 = toNorm(lastPos.current.x, lastPos.current.y);
      const n2 = toNorm(pos.x, pos.y);
      socket?.emit('annotation', { code: sessionCode, data: {
        type: 'line', color, size, x1: n1.x, y1: n1.y, x2: n2.x, y2: n2.y
      } });
      lastPos.current = pos;
    } else if (tool === 'arrow' || tool === 'circle') {
      previewRef.current = {
        type: tool, color, size,
        x1: startPos.current.x, y1: startPos.current.y,
        x2: pos.x, y2: pos.y
      };
      lastPos.current = pos;
      redraw();
    }
  };

  const onMouseDown = (e) => {
    if (tool === 'text') return;
    if (tool === 'pointer') {
      const pos = getPos(e);
      const n = toNorm(pos.x, pos.y);
      socket?.emit('annotation', { code: sessionCode, data: {
        type: 'tap', x: n.x, y: n.y, color
      } });
      return;
    }
    isDrawing.current = true;
    const pos = getPos(e);
    lastPos.current = pos;
    startPos.current = pos;
  };

  const onMouseUp = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    if (tool === 'arrow' || tool === 'circle') {
      const shape = {
        type: tool, color, size,
        x1: startPos.current.x, y1: startPos.current.y,
        x2: lastPos.current.x, y2: lastPos.current.y
      };
      previewRef.current = null;
      addLocalWithFade(shape);

      const n1 = toNorm(shape.x1, shape.y1);
      const n2 = toNorm(shape.x2, shape.y2);
      socket?.emit('annotation', { code: sessionCode, data: {
        type: tool, color, size, x1: n1.x, y1: n1.y, x2: n2.x, y2: n2.y
      } });
    }
  };

  const onMouseLeave = () => {
    isDrawing.current = false;
    previewRef.current = null;
    redraw();
    if (tool === 'pointer') {
      socket?.emit('annotation', { code: sessionCode, data: { type: 'pointer:hide' } });
    }
  };

  const onCanvasClick = (e) => {
    if (tool !== 'text') return;
    const pos = getPos(e);
    const txt = window.prompt('Tekst:');
    if (!txt) return;
    const shape = { type: 'text', color, size, x: pos.x, y: pos.y, text: txt };
    addLocalWithFade(shape);

    const n = toNorm(pos.x, pos.y);
    socket?.emit('annotation', { code: sessionCode, data: {
      type: 'text', color, size, x: n.x, y: n.y, text: txt
    } });
  };

  const clearAll = useCallback(() => {
    localAnnotations.current = [];
    previewRef.current = null;
    redraw();
    socket?.emit('annotation:clear', { code: sessionCode });
  }, [socket, sessionCode, redraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      redraw();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [redraw]);

  const colors = ['#E24B4A', '#EF9F27', '#185FA5', '#639922', '#ffffff'];
  const tools = [
    { id: 'pointer', label: '👆Pointer' },
    { id: 'pen', label: 'Olovka' },
    { id: 'arrow', label: 'Strelica' },
    { id: 'circle', label: 'Krug' },
    { id: 'text', label: 'Tekst' }
  ];

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', inset: 0,
          cursor: tool === 'text' ? 'text' : 'crosshair',
          width: '100%', height: '100%'
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onClick={onCanvasClick}
      />

      <button
        onClick={() => setToolbarVisible(v => !v)}
        style={{
          position: 'absolute', top: 10, right: 10,
          padding: '6px 12px', borderRadius: 6, border: 'none',
          background: 'rgba(20,20,20,0.75)', color: '#fff',
          fontSize: 12, fontWeight: 500, cursor: 'pointer',
          zIndex: 11, backdropFilter: 'blur(4px)'
        }}
      >
        {toolbarVisible ? '✕ Sakrij alate' : '✎ Alati'}
      </button>

      {toolbarVisible && (
        <div style={{
          position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(20,20,20,0.85)', borderRadius: 10, padding: '8px 14px',
          display: 'flex', alignItems: 'center', gap: 10, backdropFilter: 'blur(4px)',
          zIndex: 10
        }}>
          {tools.map(t => (
            <button key={t.id} onClick={() => setTool(t.id)} style={{
              padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
              background: tool === t.id ? '#185FA5' : 'rgba(255,255,255,0.15)',
              color: '#fff', fontWeight: tool === t.id ? 600 : 400
            }}>{t.label}</button>
          ))}
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)' }} />
          {colors.map(c => (
            <div key={c} onClick={() => setColor(c)} style={{
              width: 18, height: 18, borderRadius: '50%', background: c, cursor: 'pointer',
              border: color === c ? '2px solid #fff' : '2px solid transparent'
            }} />
          ))}
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)' }} />
          <button onClick={clearAll} style={{
            padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontSize: 12, background: 'rgba(255,255,255,0.15)', color: '#fff'
          }}>Obriši sve</button>
        </div>
      )}
    </div>
  );
}